import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { UserPlus, Gift, ExternalLink, ShieldCheck, Clock, XCircle, PackageCheck } from "lucide-react";

const STATUS_STYLES = {
  pending:   { icon: Clock,        cls: "text-amber-600 bg-amber-500/10",    label: "Reward pending" },
  queued:    { icon: PackageCheck, cls: "text-sky-600 bg-sky-500/10",        label: "Queued in payout" },
  paid:      { icon: ShieldCheck,  cls: "text-emerald-600 bg-emerald-500/10", label: "Paid" },
  voided:    { icon: XCircle,      cls: "text-rose-600 bg-rose-500/10",      label: "Voided (cap)" },
};

function StatusPill({ status }) {
  if (!status) {
    return <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Awaiting Pro</span>;
  }
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${s.cls}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

export function ReferralsCard({ affiliateId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!affiliateId) return;
    api.get(`/affiliates/${affiliateId}/referrals`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [affiliateId]);

  if (!data) return null;
  const { referred_by, referrals, rewards_summary, program } = data;

  return (
    <div
      data-testid="referrals-card"
      className="rounded-3xl border border-border/50 bg-card p-6 space-y-5"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <UserPlus className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold">Referral Program</div>
            <div className="text-[11px] text-muted-foreground">
              ₹{program.reward_mrp.toLocaleString("en-IN")} stack per referral that reaches Pro
              &nbsp;·&nbsp; cap {program.annual_cap}/year
              {rewards_summary.voided_count > 0 && (
                <>&nbsp;·&nbsp;<span className="text-rose-600">{rewards_summary.voided_count} voided</span></>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Slots left ({rewards_summary.year})
          </div>
          <div data-testid="referrals-slots-left" className="text-lg font-semibold">{rewards_summary.slots_left} / {program.annual_cap}</div>
        </div>
      </div>

      {/* Referred by */}
      {referred_by && (
        <div className="flex items-center justify-between rounded-2xl bg-secondary/40 px-4 py-3">
          <div className="text-xs">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Referred by</div>
            <Link
              to={`/affiliates/${referred_by.id}`}
              className="text-sm font-medium hover:underline inline-flex items-center gap-1"
            >
              {referred_by.name} <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{referred_by.tier}</span>
        </div>
      )}

      {/* Rewards summary — Paid / Queued / Pending */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-2xl bg-emerald-500/10 px-3 py-2" title="Rewards paid out — payout was marked paid">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" /> Paid MRP
          </div>
          <div className="text-lg font-semibold text-emerald-700 mt-0.5" data-testid="referrals-paid-mrp">
            ₹{rewards_summary.paid_mrp.toLocaleString("en-IN")}
          </div>
        </div>
        <div className="rounded-2xl bg-sky-500/10 px-3 py-2" title="Included in a locked payout — waiting for admin to mark paid">
          <div className="text-[10px] uppercase tracking-widest text-sky-700 flex items-center gap-1">
            <PackageCheck className="h-3 w-3" /> Queued MRP
          </div>
          <div className="text-lg font-semibold text-sky-700 mt-0.5" data-testid="referrals-queued-mrp">
            ₹{rewards_summary.queued_mrp.toLocaleString("en-IN")}
          </div>
        </div>
        <div className="rounded-2xl bg-amber-500/10 px-3 py-2" title="Reward earned — waiting for the next payout run">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Pending MRP
          </div>
          <div className="text-lg font-semibold text-amber-700 mt-0.5" data-testid="referrals-pending-mrp">
            ₹{rewards_summary.pending_mrp.toLocaleString("en-IN")}
          </div>
        </div>
      </div>

      {/* Referrals list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Their Referrals ({referrals.length})
          </div>
        </div>
        {referrals.length === 0 ? (
          <div className="text-xs text-muted-foreground italic px-1 py-3">
            Nobody referred by this affiliate yet. Add referrals from a referred affiliate&apos;s Edit dialog.
          </div>
        ) : (
          <div className="space-y-1.5">
            {referrals.map((r) => (
              <Link
                key={r.id}
                to={`/affiliates/${r.id}`}
                data-testid={`referral-row-${r.id}`}
                className="flex items-center justify-between rounded-xl border border-border/50 px-3 py-2 hover:bg-secondary/40 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                    {r.tier}
                    {r.pro_reached_at && <span className="ml-2 normal-case tracking-normal">· hit Pro {new Date(r.pro_reached_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>}
                    {r.reward_paid_at && <span className="ml-2 normal-case tracking-normal">· paid {new Date(r.reward_paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.reward_mrp ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary">
                      <Gift className="h-3 w-3" /> ₹{r.reward_mrp.toLocaleString("en-IN")}
                    </span>
                  ) : null}
                  <StatusPill status={r.reward_status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
